import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/Seo";
import { SupplyProcess } from "@/components/home/SupplyProcess";
import { CommercialTerms } from "@/components/home/CommercialTerms";
import { WhyUs } from "@/components/home/WhyUs";
import { breadcrumbSchema } from "@/lib/schema";
import { Reveal } from "@/components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "How is a grade confirmed before an order?",
    a: "We check the lot physically available, share actual-lot photographs or a sample, and state the agreed specification in the written quotation. Supply is then made against that approved specification.",
  },
  {
    q: "Why are prices not published on the website?",
    a: "Spice rates change with market conditions and differ from lot to lot. Publishing a fixed price would be misleading, so every requirement receives a current quotation with its own validity period.",
  },
  {
    q: "What are the payment terms?",
    a: "Orders are accepted against advance payment or payment confirmed before dispatch. Credit is not available by default. Dispatch begins after payment clearance is confirmed.",
  },
  {
    q: "Can I get a sample before ordering?",
    a: "Yes. We can share lot photographs or dispatch a physical sample drawn from the available lot so that you can approve the material before committing.",
  },
  {
    q: "Do you supply outside Telangana?",
    a: "Our primary market is Hyderabad and Telangana. Orders for other locations in India are considered subject to quantity, logistics, payment confirmation and commercial feasibility.",
  },
  {
    q: "What happens if the delivered material differs from the approved sample?",
    a: "Raise the concern within 48 hours of delivery, before the material is processed or resold, with photographs and the retained sample. Where a variance from the confirmed specification is established, we offer a replacement, price adjustment or refund of the affected portion as mutually agreed.",
  },
];

export default function QualityProcessPage() {
  return (
    <>
      <Seo
        title="Quality & Supply Process | Nandanaaya Spices Hyderabad"
        description="How Nandanaaya Spices inspects lots, segregates grades, shares samples, quotes rates and dispatches wholesale spice orders from Hyderabad, including payment terms."
        path="/quality-and-process"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            breadcrumbSchema([{ name: "Quality & Process", url: "/quality-and-process" }]),
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }}
      />
      <PageShell
        testid="quality-process-page"
        title="Quality & Supply Process"
        subtitle="Lot inspection, grade segregation, sample approval, written quotation and dispatch — the full workflow behind every order."
        breadcrumbs={[{ label: "Quality & Process" }]}
      >
        <section className="py-12 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto space-y-4">
            <Reveal className="space-y-3">
              <h2 className="text-xl font-bold text-spice-ink">Our approach to quality</h2>
              <p className="text-sm text-spice-muted leading-relaxed">
                We describe material by what can be seen and verified in the lot: appearance, colour tone, pod or berry
                size, cleanliness and condition. We do not publish laboratory results or oil-content figures unless a
                test report is supplied with the specific lot concerned.
              </p>
              <p className="text-sm text-spice-muted leading-relaxed">
                Grade and availability are confirmed before the order, material is carefully handled and packed, and
                weighments are recorded on the order documents. Where a buyer needs something specific — mixed material,
                segregated sizes or particular packing — it is agreed in writing before dispatch.
              </p>
            </Reveal>
          </div>
        </section>

        <WhyUs />
        <SupplyProcess />
        <CommercialTerms />

        <section className="py-14 px-4 sm:px-6">
          <div className="max-w-3xl mx-auto space-y-6">
            <Reveal className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-widest text-spice-green">Common questions</span>
              <h2 className="text-2xl font-bold text-spice-ink">Buyer FAQs</h2>
            </Reveal>
            <Reveal>
              <Accordion type="single" collapsible className="w-full" data-testid="quality-faq-accordion">
                {FAQS.map((faq, i) => (
                  <AccordionItem key={faq.q} value={`faq-${i}`} className="border-spice-line">
                    <AccordionTrigger className="text-left text-sm font-semibold text-spice-ink hover:text-spice-green" data-testid={`faq-trigger-${i + 1}`}>
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-xs text-spice-muted leading-relaxed">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      </PageShell>
    </>
  );
}
